if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, this.ownFieldMessages(event.errors));
          }
        });

        [this.nameInput, this.emailInput].forEach((input) =>
          input?.addEventListener('input', () => {
            if (input.getAttribute('aria-invalid') === 'true' && !this.fieldError(input)) this.clearFieldError(input);
          })
        );
      }

      // The form is novalidate and posted via fetch, and Shopify accepts an empty recipient - see known-issues.md: Gift card.
      validate() {
        this.clearErrorMessage();

        const errors = {};
        [this.nameInput, this.emailInput].forEach((input) => {
          const error = input && this.fieldError(input);
          if (error) errors[input === this.nameInput ? 'name' : 'email'] = [error];
        });

        const fields = Object.keys(errors);
        if (!fields.length) return true;

        this.displayErrorMessage(this.defaultErrorHeader, errors);
        this[`${fields[0]}Input`].focus();
        return false;
      }

      fieldError(input) {
        const value = input.value.trim();
        if (input === this.nameInput) return value ? null : this.dataset.nameRequired;
        if (!value) return this.dataset.emailRequired;
        // Stricter than type="email": Shopify rejects "asd@a", but only after the request.
        if (!input.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return this.dataset.emailInvalid;
        return null;
      }

      // Shopify's field errors are fragments ("is invalid") that read as broken on their own.
      ownFieldMessages(errors) {
        if (!errors || typeof errors !== 'object') return errors;

        const own = { name: this.dataset.nameRequired, email: this.dataset.emailInvalid };
        return Object.fromEntries(
          Object.entries(errors).map(([key, value]) => [key, own[key] ? [own[key]] : value])
        );
      }

      clearFieldError(input) {
        const message = this.querySelector(`#${input.getAttribute('aria-describedby')}`);
        message?.classList.add('hidden');
        input.setAttribute('aria-invalid', false);
        input.removeAttribute('aria-describedby');

        if (!this.querySelector('[aria-invalid="true"]')) this.clearErrorMessage();
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      // The summary box is only for errors with no field to sit under (data-hide-errors leaves nowhere else).
      displayErrorMessage(title, body) {
        this.clearErrorMessage();

        if (typeof body !== 'object') {
          this.showSummary(body);
          return;
        }

        const unmatched = [];
        Object.entries(body).forEach(([key, value]) => {
          const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
          const message = `${value.join(', ')}`;
          const errorMessageElement = this.querySelector(`#${errorMessageId}`);
          const errorTextElement = errorMessageElement?.querySelector('.error-message');
          if (!errorTextElement) {
            unmatched.push(message);
            return;
          }

          errorTextElement.innerText = `${message}.`;
          errorMessageElement.classList.remove('hidden');

          const inputElement = this[`${key}Input`];
          if (!inputElement) return;

          inputElement.setAttribute('aria-invalid', true);
          inputElement.setAttribute('aria-describedby', errorMessageId);
        });

        if (unmatched.length) this.showSummary(unmatched.join(' '));
      }

      showSummary(message) {
        this.errorMessageWrapper.hidden = false;
        this.errorMessage.innerText = message;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        this.inputFields().forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        this.clearInputFields();
        this.clearErrorMessage();
      }
    }
  );
}
